import "./doctree.css";

/**
 * A ```tree fenced block rendered as a real node tree.
 *
 * The source stays a plain code block drawn with box-drawing characters, so
 * GitHub (and any other Markdown viewer) shows the same hierarchy as ASCII art.
 * Here we parse that art back into nodes and render connectors with CSS.
 *
 *     provider — Intility itself
 *     └── organization — a tenant
 *         └── company — a business entity
 *
 * Depth comes from the indentation before the connector (4 spaces per level).
 * Everything after the first em dash is the node's description, rendered as a
 * second line inside that node's own card.
 */
export interface TreeNode {
  label: string;
  description?: string;
  children: TreeNode[];
}

const LINE = /^([\s│]*)([└├]──\s*)?(.*)$/;
const INDENT_PER_LEVEL = 4;

export function parseTree(source: string): TreeNode[] {
  const roots: TreeNode[] = [];
  // stack[d] is the node most recently seen at depth d, i.e. the parent for
  // the next node at depth d + 1.
  const stack: TreeNode[] = [];

  // Split on both line endings. The docs are read with `?raw`, so a CRLF
  // checkout hands us "\r"-terminated lines, and `.` in LINE does not match
  // "\r" (it is a line terminator) — every line but the last would fail to
  // match at all, leaving a one-node tree.
  for (const raw of source.split(/\r?\n/)) {
    if (raw.trim() === "") continue;
    const [, prefix, connector, rest] = LINE.exec(raw) ?? [];
    if (!rest) continue;

    // The connector itself ("└── ") is one level of indentation; the spaces
    // and trunk characters before it account for the levels above that.
    const depth =
      Math.round(prefix.length / INDENT_PER_LEVEL) + (connector ? 1 : 0);
    const [label, ...description] = rest.split("—");
    const node: TreeNode = {
      label: label.trim(),
      description: description.join("—").trim() || undefined,
      children: [],
    };

    const parent = depth > 0 ? stack[depth - 1] : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);

    stack[depth] = node;
    stack.length = depth + 1;
  }

  return roots;
}

/** The nodes of each level, top level first. */
export function levelsOf(nodes: TreeNode[]): TreeNode[][] {
  const levels: TreeNode[][] = [];
  const walk = (level: TreeNode[], depth: number) => {
    if (level.length === 0) return;
    levels[depth] = [...(levels[depth] ?? []), ...level];
    for (const node of level) walk(node.children, depth + 1);
  };
  walk(nodes, 0);
  return levels;
}

function TreeLevel({ nodes, depth }: { nodes: TreeNode[]; depth: number }) {
  // Deliberately divs, not ul/li: the prose rules in markdown.css style every
  // list inside `.md-body` (`padding-left`, item margins, nested-list margins),
  // and a diagram that has to out-specify all of them is one new prose rule away
  // from breaking. A tree figure is not a list, so it opts out of being one.
  return (
    <div className="md-tree-level">
      {nodes.map((node) => (
        <div
          className="md-tree-item"
          key={`${node.label}-${node.description ?? ""}`}
        >
          {depth > 0 && <div className="md-tree-arrow" />}
          <div className="md-tree-node" data-tree-depth={depth}>
            <p className="bf-h5 md-tree-node-label">{node.label}</p>
            {node.description && (
              <p className="bf-p md-tree-node-description">
                {node.description}
              </p>
            )}
          </div>
          {node.children.length > 0 && (
            <TreeLevel nodes={node.children} depth={depth + 1} />
          )}
        </div>
      ))}
    </div>
  );
}

export function DocTree({ source }: { source: string }) {
  const nodes = parseTree(source);
  if (nodes.length === 0) return null;

  return (
    <div className="md-tree">
      <div className="md-tree-canvas">
        <TreeLevel nodes={nodes} depth={0} />
      </div>
    </div>
  );
}
